import { LexicalPromptInputHandle } from '../components/editor/prompt-input/LexicalPromptInput';
import { 
  PromptItemData, 
  enrichPromptItemsWithContext,
  formatPromptItemsPreview 
} from '../utils/promptItemContext';

/**
 * PreparedPrompt - The result of preparing a prompt for submission
 */
export interface PreparedPrompt {
  text: string;              // The cleaned prompt text (for storage/display)
  rawText: string;           // Original prompt with markers
  promptItems: PromptItemData[];  // Enriched items to send separately
  hasContextItems: boolean;  // Whether any context items were included
}

/**
 * PromptPreparationService - Encapsulates all prompt item extraction and enrichment logic
 * 
 * This service owns the responsibility of:
 * - Extracting prompt items from the Lexical editor
 * - Enriching items with element context from the iframe
 * - Formatting the context into a readable string for the AI
 * - Returning a complete, enriched prompt ready for submission
 */
class PromptPreparationService {
  /**
   * Prepare a prompt for submission by extracting and enriching all prompt items
   * 
   * @param editorRef - Reference to the Lexical editor
   * @param iframeRef - Reference to the edit mode iframe (for element context)
   * @param code - PREPROCESSED HTML with data-edit-ids (for accurate line number lookup)
   *               This should be the same HTML that's in the iframe, not the original editor code
   * @param rawPromptText - The raw prompt text from the editor
   * @returns PreparedPrompt with enriched text
   */
  prepare(
    editorRef: LexicalPromptInputHandle | null,
    iframeRef: React.RefObject<HTMLIFrameElement> | undefined,
    rawPromptText: string
  ): PreparedPrompt {
    // If no editor ref, return raw prompt
    if (!editorRef) {
      return {
        text: rawPromptText,
        rawText: rawPromptText,
        promptItems: [],
        hasContextItems: false,
      };
    }

    // Extract prompt items from the Lexical editor
    const rawItems = editorRef.getPromptItems();
    
    // If no items, return prompt as-is
    if (rawItems.length === 0) {
      return {
        text: rawPromptText,
        rawText: rawPromptText,
        promptItems: [],
        hasContextItems: false,
      };
    }

    // Enrich items with context (HTML, CSS, comp-name, etc.)
    const enrichedItems = this.enrichItems(rawItems, iframeRef);
    
    return {
      text: rawPromptText,           // Keep markers for storage (ChatHistory will render them)
      rawText: rawPromptText,        // Same as text
      promptItems: enrichedItems,    // Items to send separately to server
      hasContextItems: true,
    };
  }

  /**
   * Enrich prompt items with their full context
   */
  private enrichItems(
    items: PromptItemData[],
    iframeRef: React.RefObject<HTMLIFrameElement> | undefined
  ): PromptItemData[] {
    if (!iframeRef) {
      return items;
    }
    
    return enrichPromptItemsWithContext(items, iframeRef);
  }


  /**
   * Clean item markers from prompt text
   * Removes __PROMPT_ITEM[...] markers that Lexical generates
   */
  private cleanItemMarkers(prompt: string): string {
    return prompt.replace(/__PROMPT_ITEM\[(?:[^\]\\]|\\.)*\]__/g, '').trim();
  }
}

// Export singleton instance
export const promptPreparationService = new PromptPreparationService();

